import { Alert, AlertTitle, Button, Divider, Grid, Typography } from '@mui/material';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import {
  DateInput,
  Edit,
  FunctionField,
  Labeled,
  NumberInput,
  ReferenceField,
  ReferenceInput,
  SelectField,
  SelectInput,
  SimpleForm,
  TextField,
  TextInput,
  AutocompleteInput,
} from 'react-admin';
import { Permission } from '../../components';
import { PERMISSIONS } from '../../constants';
import { InvoiceStatus } from '../../models/invoiceStatus';
import { PartnerPaymentStatus } from '../../models/partnerPaymentStatus';
import { ClientBillType } from '../../models/clientBillType';
import { GestaoClickStore } from '../../models/gestaoClickStore';
import { filterToQuery } from '../../utils';

/**
 * A competência é o mês do vencimento do boleto no Gestão Click: toda sincronização reescreve
 * `monthDate` a partir da `data_vencimento` do ERP. Por isso o campo é somente leitura aqui — editar
 * só deste lado seria desfeito na execução seguinte do billing.
 */
const CompetenceNotice = () => (
  <Alert severity="warning" sx={{ mb: 2, width: '100%' }}>
    <AlertTitle>A competência é o mês do vencimento no Gestão Click</AlertTitle>
    O mês acompanha a <strong>data de vencimento do recebimento no Gestão Click</strong>. Para mudar, ajuste lá e
    depois clique em <strong>Atualizar Cobranças</strong> no relatório de cobranças. O mês aqui é
    reescrito a partir do ERP a cada sincronização — alterar só deste lado não tem efeito.
  </Alert>
);

const Section = ({ title }: { title: string }) => (
  <Grid size={12} sx={{ mt: 1 }}>
    <Typography variant="subtitle2" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
      {title}
    </Typography>
    <Divider sx={{ mt: 0.5 }} />
  </Grid>
);

const ClientBillEdit = () => {
  return (
    <Permission permission={PERMISSIONS.ADMIN}>
      {/* redirect={false} — o resource client-bills não tem tela de lista; sem isso o save
          jogava o usuário em /client-bills (rota inexistente). Fica na própria edição. */}
      <Edit title="Editar Cobrança" redirect={false} mutationMode="pessimistic">
        <SimpleForm>
          <CompetenceNotice />

          <Grid container spacing={2} sx={{ width: '100%' }}>
            <Section title="Identificação" />

            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <Labeled label="Rede" fullWidth>
                <ReferenceField source="chainId" reference="chains" link={false} emptyText="—">
                  <TextField source="name" />
                </ReferenceField>
              </Labeled>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <Labeled label="Posto" fullWidth>
                <ReferenceField source="placeId" reference="stations" link={false} emptyText="—">
                  <TextField source="name" />
                </ReferenceField>
              </Labeled>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <Labeled label="Loja (Gestão Click)" fullWidth>
                <SelectField
                  source="gestaoClickStoreId"
                  emptyText="—"
                  choices={GestaoClickStore.GestaoClickStoreChoices}
                />
              </Labeled>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <TextInput
                source="monthDate"
                label="Competência"
                readOnly
                disabled
                fullWidth
                helperText="Mês do vencimento no Gestão Click"
              />
            </Grid>

            <Section title="Cobrança do cliente" />

            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <SelectInput
                source="type"
                label="Tipo"
                choices={ClientBillType.ClientBillTypeChoices}
                fullWidth
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <NumberInput source="value" label="Valor do boleto" fullWidth />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <SelectInput
                source="status"
                label="Status Cliente"
                choices={InvoiceStatus.InvoiceStatusChoices}
                fullWidth
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <DateInput source="dueDate" label="Vencimento" fullWidth />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <DateInput source="paymentDate" label="Data de pagamento" fullWidth />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <TextInput source="paymentMethod" label="Método de pagamento" fullWidth />
            </Grid>

            <Section title="Parceiro / Revenda" />

            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <ReferenceInput
                source="retailerId"
                reference="retailers"
                sort={{ field: 'name', order: 'ASC' }}
                perPage={null}
              >
                <AutocompleteInput
                  filterToQuery={filterToQuery}
                  label="Revenda"
                  optionText="name"
                  fullWidth
                />
              </ReferenceInput>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <NumberInput source="retailerValue" label="Valor do Parceiro" fullWidth />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <SelectInput
                source="partnerStatus"
                label="Status Parceiro"
                choices={PartnerPaymentStatus.PartnerPaymentStatusChoices}
                fullWidth
              />
            </Grid>

            <Section title="Gestão Click" />

            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <TextInput source="gestaoClickId" label="ID Gestão Click" fullWidth />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 8 }} sx={{ display: 'flex', alignItems: 'center' }}>
              <FunctionField
                label=""
                render={record => record?.gestaoClickId ? (
                  <Button
                    size="small"
                    startIcon={<OpenInNewIcon />}
                    onClick={() => window.open(`https://gestaoclick.com/financeiro/movimentacoes_financeiras/visualizar_recebimento/${record.gestaoClickId}?retorno=%2Fmovimentacoes_financeiras%2Findex_recebimento`, '_blank')}
                  >
                    Abrir no Gestão Click
                  </Button>
                ) : null}
              />
            </Grid>
          </Grid>
        </SimpleForm>
      </Edit>
    </Permission>
  );
};

export default ClientBillEdit;
